import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  RateReview as RateReviewIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase-config';

/**
 * NotificationBell - Real-time notification system
 *
 * Phase 5.5: Displays notification badge and dropdown menu
 * with real-time updates via Supabase subscription
 *
 * @param {string} userId - Current user's ID
 * @param {function} onNotificationClick - Callback when notification clicked
 */
const NotificationBell = ({ userId, onNotificationClick }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markingRead, setMarkingRead] = useState(false);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('approval_notifications')
        .select('*')
        .eq('target_user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (fetchError) throw fetchError;
      setNotifications(data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Set up real-time subscription
  useEffect(() => {
    if (!userId) return;

    fetchNotifications();

    // Subscribe to new notifications
    const subscription = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'approval_notifications',
          filter: `target_user_id=eq.${userId}`
        },
        (payload) => {
          setNotifications((prev) => [payload.new, ...prev].slice(0, 20));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'approval_notifications',
          filter: `target_user_id=eq.${userId}`
        },
        (payload) => {
          setNotifications((prev) =>
            prev.map((n) =>
              n.notification_id === payload.new.notification_id ? payload.new : n
            )
          );
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId, fetchNotifications]);

  // Calculate unread count
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications]
  );

  // Handle menu open/close
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const { error: updateError } = await supabase
        .from('approval_notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('notification_id', notificationId);

      if (updateError) throw updateError;

      setNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      setMarkingRead(true);
      const { error: updateError } = await supabase
        .from('approval_notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('target_user_id', userId)
        .eq('is_read', false);

      if (updateError) throw updateError;

      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          is_read: true,
          read_at: n.read_at || new Date().toISOString()
        }))
      );
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    } finally {
      setMarkingRead(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      await markAsRead(notification.notification_id);
    }

    // Navigate or callback
    if (notification.action_url) {
      navigate(notification.action_url);
    } else if (onNotificationClick) {
      onNotificationClick(notification);
    }

    handleClose();
  };

  // Get notification icon based on type
  const getNotificationIcon = (type, priority) => {
    const iconProps = {
      fontSize: 'small',
      color: priority === 'high' ? 'error' : priority === 'medium' ? 'warning' : 'primary'
    };

    switch (type) {
      case 'approval_required':
        return <AssignmentIcon {...iconProps} />;
      case 'revision_requested':
        return <RateReviewIcon {...iconProps} />;
      case 'approved':
        return <CheckCircleIcon {...iconProps} color="success" />;
      case 'rejected':
        return <ErrorIcon {...iconProps} color="error" />;
      case 'reminder':
        return <WarningIcon {...iconProps} color="warning" />;
      default:
        return <InfoIcon {...iconProps} />;
    }
  };

  // Get priority chip
  const getPriorityChip = (priority) => {
    if (!priority || priority === 'low') return null;

    const colors = {
      high: 'error',
      medium: 'warning'
    };

    return (
      <Chip
        label={priority.toUpperCase()}
        size="small"
        color={colors[priority] || 'default'}
        sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
      />
    );
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}>
        <IconButton
          color="inherit"
          onClick={handleOpen}
          aria-label={`${unreadCount} notifications`}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            invisible={unreadCount === 0}
          >
            {unreadCount > 0 ? (
              <NotificationsActiveIcon color="action" />
            ) : (
              <NotificationsIcon />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
            overflow: 'auto'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'grey.50'
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={markingRead ? <CircularProgress size={14} /> : <DoneAllIcon />}
              onClick={markAllAsRead}
              disabled={markingRead}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {/* Loading state */}
        {loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Loading notifications...
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Empty state */}
        {!loading && !error && notifications.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography color="text.secondary">No notifications</Typography>
          </Box>
        )}

        {/* Notification list */}
        {!loading && !error && notifications.length > 0 && (
          <List sx={{ p: 0 }}>
            {notifications.map((notification) => (
              <ListItem
                key={notification.notification_id}
                disablePadding
                sx={{
                  bgcolor: notification.is_read ? 'transparent' : 'action.hover'
                }}
              >
                <ListItemButton
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.selected'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getNotificationIcon(notification.notification_type, notification.priority)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant="body2"
                          fontWeight={notification.is_read ? 400 : 600}
                          sx={{ flex: 1 }}
                        >
                          {notification.title}
                        </Typography>
                        {getPriorityChip(notification.priority)}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 260
                          }}
                        >
                          {notification.body}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {formatTimeAgo(notification.created_at)}
                          {notification.is_read && (
                            <DoneIcon
                              sx={{
                                fontSize: 12,
                                ml: 0.5,
                                verticalAlign: 'middle',
                                color: 'success.main'
                              }}
                            />
                          )}
                        </Typography>
                      </>
                    }
                  />
                  {notification.action_url && (
                    <OpenInNewIcon
                      sx={{ fontSize: 14, color: 'action.active', ml: 1 }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button
                size="small"
                onClick={() => {
                  navigate('/approvals');
                  handleClose();
                }}
              >
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

/**
 * NotificationItem - Individual notification display (for full page view)
 */
export const NotificationItem = ({ notification, onMarkRead, onNavigate }) => {
  const getTypeIcon = (type) => {
    switch (type) {
      case 'approval_required':
        return <AssignmentIcon />;
      case 'revision_requested':
        return <RateReviewIcon />;
      case 'approved':
        return <CheckCircleIcon color="success" />;
      case 'rejected':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon />;
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Box
      sx={{
        p: 2,
        mb: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: notification.is_read ? 'grey.200' : 'primary.main',
        bgcolor: notification.is_read ? 'transparent' : 'primary.50',
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'action.hover'
        }
      }}
      onClick={() => onNavigate?.(notification)}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ color: notification.is_read ? 'text.secondary' : 'primary.main' }}>
          {getTypeIcon(notification.notification_type)}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight={notification.is_read ? 400 : 600}
            >
              {notification.title}
            </Typography>
            {notification.priority === 'high' && (
              <Chip label="URGENT" size="small" color="error" />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {notification.body}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            {formatDate(notification.created_at)}
          </Typography>
        </Box>
        {!notification.is_read && (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead?.(notification.notification_id);
            }}
          >
            Mark Read
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(NotificationBell);
